import type { ReactNode } from "react";
import { Archive, ArrowRight, CheckCircle2, ChevronDown, Copy } from "lucide-react";

import { speciesName } from "@/data/speciesNames";
import {
  VariantBadge,
  variantColor as vColor,
  variantAlpha as vAlpha,
} from "@/components/dexVariantVisuals";
import { ChanceOnlyBadge } from "@/components/trade/ChanceOnlyBadge";
import { CompareSprite } from "@/components/trade/CompareSprite";
import type { PlannedTrade } from "@/components/trade/compareTypes";
import { dexNumberLabel } from "@/lib/dexNumberLabel";

/**
 * Trade-list block (b20). The ACTIVE trades a trainer has earmarked with one
 * friend. Parked trades are excluded — they have their own block.
 *
 * `variant` picks how much the block ASSERTS itself, not how it is painted —
 * every card on the trade panes now wears the same soft chrome, because a page
 * with four different card treatments reads as four unrelated widgets. It used
 * to give the `hero` a stronger frame; that made the centrepiece look like it
 * belonged to a different screen than the blocks under it.
 *
 *   `soft` — quiet: renders nothing at all when the list is empty.
 *   `hero` — names the friend under the eyebrow, and holds its place when empty
 *            (pass `emptyState`), because it is the first thing under the
 *            heading and a gap there reads as a broken page.
 */
export function TradeListBlock({
  trades,
  friendName,
  showAll,
  onToggleShowAll,
  onSearch,
  onPark,
  onUntick,
  variant = "soft",
  emptyState,
}: {
  trades: PlannedTrade[];
  friendName: string;
  showAll: boolean;
  onToggleShowAll: () => void;
  onSearch: (t: PlannedTrade) => void;
  /** "Remove" — parks the trade rather than deleting it. See TradeRow. */
  onPark: (t: PlannedTrade) => void;
  /** Unticking un-earmarks: the row goes, the lead returns below. See TradeRow. */
  onUntick: (t: PlannedTrade) => void;
  /** Chrome only — see the block comment. Defaults to Compare's quiet card. */
  variant?: "soft" | "hero";
  /**
   * Rendered in place of the rows when the list is empty. Omit (Compare's
   * behaviour) and an empty list renders nothing at all.
   */
  emptyState?: ReactNode;
}) {
  // Parked trades are NOT shown here — they have their own block (see
  // ParkedTradesBlock). They used to sit at the bottom of this list, dimmed,
  // which made "removed" look like "still on the list, but sad".
  const ordered = trades.filter((t) => t.status !== "hidden");
  const active = ordered;
  const visible = showAll ? ordered : ordered.slice(0, 6);
  const isHero = variant === "hero";

  // Compare's contract: no trades, no block. The hero presentation opts out —
  // an empty centrepiece still has to tell you how to fill it.
  if (ordered.length === 0 && !emptyState) return null;

  return (
    <section
      className="v2-neon-card-soft p-4 mt-4"
      data-testid="trade-list-block"
    >
      {/* Header doubles as a collapse control once the list is expanded, so a
          long list can be re-collapsed from the top without scrolling down. */}
      <button
        type="button"
        onClick={() => ordered.length > 6 && onToggleShowAll()}
        disabled={ordered.length <= 6}
        aria-expanded={showAll}
        data-testid="collapse-trade-list"
        className="w-full flex items-center justify-between mb-2.5 text-left"
      >
        <span className="min-w-0">
          <span className="v2-eyebrow" data-testid="eyebrow-trade-list">
            <span className="v2-eyebrow__dot" aria-hidden />
            Your trade list
          </span>
          {/* The hub is a per-friend page, so the list has to name whose it is —
              otherwise two friends' lists look identical in a screenshot. */}
          {isHero && (
            <span
              className="block text-[11.5px] mt-1.5 truncate"
              style={{ color: "var(--text-ink-faint)" }}
              data-testid="trade-list-friend"
            >
              Earmarked with {friendName}
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] font-mono tabular-nums" style={{ color: "var(--text-ink-faint)" }}>
            {active.length}
          </span>
          {ordered.length > 6 && (
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showAll ? "rotate-180" : ""}`}
              style={{ color: "var(--text-ink-faint)" }}
            />
          )}
        </span>
      </button>

      {ordered.length === 0 ? (
        <div data-testid="trade-list-empty">{emptyState}</div>
      ) : (
        // pl/pr-3 leaves room for the direction arrow, which straddles the row's
        // border and would otherwise be clipped by the card's own padding.
        <div className="flex flex-col gap-2.5 px-3">
          {visible.map((t) => (
            <TradeRow
              key={t.id}
              trade={t}
              friendName={friendName}
              onSearch={onSearch}
              onPark={onPark}
              onUntick={onUntick}
            />
          ))}
        </div>
      )}

      {ordered.length > 6 && (
        <button
          type="button"
          onClick={onToggleShowAll}
          data-testid="trade-list-view-all"
          className="mt-2 w-full rounded-full text-[11px] font-semibold py-2 inline-flex items-center justify-center gap-1.5"
          style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)", minHeight: 34 }}
        >
          {showAll ? "Show fewer" : `View all ${ordered.length}`}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAll ? "rotate-180" : ""}`} />
        </button>
      )}
    </section>
  );
}

/**
 * One earmarked trade.
 *
 * DIRECTION IS THE THING TO GET RIGHT. A trade list is two piles that look
 * identical — things leaving and things arriving — and the old row said which
 * with a 14px arrow at the far right of a crowded strip, pointing left or right.
 * Nobody reads that. Direction is now carried three ways at once, largest first:
 *
 *   1. A big arrow STRADDLING the row's edge, on the side the Pokémon travels
 *      through: the RIGHT edge for a give (it is leaving), the LEFT edge for a
 *      get (it is arriving). Half in, half out, so the eye catches it before it
 *      reaches the text. Both arrows point RIGHT — the SIDE carries the
 *      direction, and flipping the glyph too made a get read as "going back"
 *      rather than "coming in".
 *   2. That same edge is a thick accent stripe — the edge the trade moves
 *      through is the loud one, so a column of rows sorts itself at a glance.
 *   3. The words, which are now the confirmation rather than the only signal.
 *
 * Actions are down to two. "Done" and "Hide" went: completion is handled by the
 * proposal flow on the next pane, and "Hide" and "Remove" were two names for
 * nearly the same act with opposite meanings — Remove DELETED the row and put
 * the lead straight back in the lists below, which is the opposite of what
 * "remove it from my list" means to anyone. Remove now parks (see the Parked
 * block), and Search copies a GO string for this exact copy.
 *
 * THE TICK IS THE UNDO OF THE LEAD TILE'S CIRCLE. Every lead below carries an
 * empty circle you tap to earmark it; this row is that lead, ticked, so tapping
 * the tick un-earmarks it and the lead goes straight back to "you have" /
 * "they have". Parking it away for good is the deliberate act next to it — but
 * an accidental tap on a lead had no cheap way back once Remove stopped
 * deleting, which is what made adding one feel like a commitment. It keeps the
 * tile's LEFT position in every row, so a column of them reads as one column.
 */
function TradeRow({
  trade: t,
  friendName,
  onSearch,
  onPark,
  onUntick,
}: {
  trade: PlannedTrade;
  friendName: string;
  onSearch: (t: PlannedTrade) => void;
  onPark: (t: PlannedTrade) => void;
  onUntick: (t: PlannedTrade) => void;
}) {
  const give = t.direction === "give";
  return (
    <div
      data-testid={`trade-list-row-${t.id}`}
      data-direction={t.direction}
      // A "get" row's arrow straddles the LEFT border and reaches 13px into the
      // row, so that side gets a wider gutter — enough for the tick to clear it
      // and still sit where every other row's tick sits.
      className={`relative rounded-xl py-2 ${give ? "px-3" : "pl-5 pr-3"}`}
      style={{
        background: "var(--surface-dark-7)",
        border: `1px solid ${vAlpha(t.variant, 0.4)}`,
        // The travelled edge, thickened. borderLeft/Right override the shorthand
        // above for that one side only.
        ...(give
          ? { borderRight: "3px solid var(--accent-fill)" }
          : { borderLeft: "3px solid var(--accent-fill)" }),
      }}
    >
      {/* Straddles the border: half inside the row, half outside it. */}
      <span
        className="absolute inline-flex items-center justify-center rounded-full"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          ...(give ? { right: -13 } : { left: -13 }),
          width: 26,
          height: 26,
          background: "var(--surface-sunken)",
          border: "2px solid var(--accent-fill)",
          color: "var(--accent-fill)",
        }}
        data-testid={`trade-direction-${t.id}`}
        aria-hidden
      >
        <ArrowRight className="w-3.5 h-3.5" />
      </span>

      <div className="flex items-center gap-2">
        {/* Ticked, because being in this list IS the tick. Its own button rather
            than a row-wide toggle: the row also carries Search and Remove, and a
            whole-row tap that silently un-earmarks would fire every time someone
            went for one of those. The negative margins buy the tap target back
            from the row's own padding, so it is finger-sized without moving
            anything around it. */}
        <button
          type="button"
          onClick={() => onUntick(t)}
          data-testid={`trade-untick-${t.id}`}
          aria-label={`Remove ${speciesName(t.speciesId)} from your trade list`}
          className="shrink-0 -my-2 -ml-2 -mr-1 p-2 inline-flex items-center justify-center rounded-full"
        >
          <CheckCircle2 className="w-[18px] h-[18px]" style={{ color: vColor(t.variant) }} />
        </button>
        <div
          className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
          style={{ background: "var(--surface-dark-5)", border: `1px solid ${vAlpha(t.variant, 0.4)}` }}
        >
          <CompareSprite id={t.speciesId} shiny={t.variant === "shiny"} size={30} formId={t.formId} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-bold leading-tight truncate" style={{ color: "var(--text-ink-bright)" }}>
            <span style={{ color: "var(--accent-fill)", fontWeight: 800 }}>{give ? "GIVE" : "GET"}</span>{" "}
            {speciesName(t.speciesId)}{" "}
            <span style={{ color: "var(--text-ink-faint)", fontWeight: 600 }}>
              {give ? "to" : "from"} {friendName}
            </span>
          </p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="font-mono text-[9.5px]" style={{ color: vColor(t.variant) }}>
              {dexNumberLabel(t.speciesId)}
            </span>
            <VariantBadge variant={t.variant} />
            {/* Lucky and 100% are produced BY the trade, not carried through
                it, so the row has to say it is planning an outcome rather than
                an object. See lib/tradeChance. */}
            <ChanceOnlyBadge variant={t.variant} />
          </div>
        </div>
      </div>
      <div className="flex gap-1.5 mt-2">
        <TradeListAction
          testId={`trade-search-${t.id}`}
          onClick={() => onSearch(t)}
          icon={<Copy className="w-3 h-3" />}
          label="Search"
        />
        <TradeListAction
          testId={`trade-remove-${t.id}`}
          onClick={() => onPark(t)}
          icon={<Archive className="w-3 h-3" />}
          label="Remove"
        />
      </div>
    </div>
  );
}

function TradeListAction({
  onClick,
  icon,
  label,
  tone,
  testId,
}: {
  onClick: () => void;
  icon: ReactNode;
  label: string;
  tone?: "accent" | "danger";
  testId?: string;
}) {
  const style =
    tone === "accent"
      ? { background: "var(--accent-fill-glow-subtle)", border: "1px solid var(--accent-fill-glow)", color: "var(--accent-fill)" }
      : tone === "danger"
        ? { background: "var(--surface-dark-9)", border: "1px solid hsl(354 78% 56% / 0.35)", color: "hsl(354 78% 56%)" }
        : { background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)" };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex-1 rounded-full text-[10.5px] font-semibold py-1.5 inline-flex items-center justify-center gap-1 min-h-[30px]"
      style={style}
    >
      {icon} {label}
    </button>
  );
}
